// @wahala/vatar-client: the only code that touches window.Vatar. Loads the SDK from lagoslife.app
// (never a vendored copy), throttles to the player's own limits so we get a clean local error
// instead of a rate_limited round trip, and gives the rest of the app plain promises.
export const SDK_URL = 'https://lagoslife.app/sdk/vatar-games-v1.js';
/** Hosts that only ever mean this machine. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
const isLocal = (u) => (u.protocol === 'http:' || u.protocol === 'https:') && LOCAL_HOSTS.has(u.hostname);
/**
 * Where to load the SDK from. Always lagoslife.app, with one exception for local development (the
 * PF-12 harness in packages/vatar-harness): a `vatarSdk` query parameter naming an SDK copy is
 * honoured only when the game page itself is served from this machine and the copy is too. A
 * deployed page is never on localhost, so it can never be talked into loading another script.
 */
export function sdkUrlFor(pageUrl) {
    try {
        const page = new URL(pageUrl);
        const asked = page.searchParams.get('vatarSdk');
        if (!isLocal(page) || !asked)
            return SDK_URL;
        const sdk = new URL(asked);
        if (!isLocal(sdk) || sdk.username || sdk.password)
            return SDK_URL;
        return sdk.href;
    }
    catch {
        return SDK_URL;
    }
}
/** Read once, as the page loads, before the app tidies its address. */
const PAGE_URL = typeof location === 'undefined' ? '' : location.href;
export class VatarClientError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
/** Are we inside the Lagos Life game player? Outside it every Vatar call rejects with not_framed. */
export function isFramed() {
    try {
        return window.self !== window.top;
    }
    catch {
        return true; // a cross-origin parent throws on access: that's a frame
    }
}
let loading = null;
function sdk() {
    if (!isFramed())
        return Promise.reject(new VatarClientError('not_framed'));
    loading ??= new Promise((resolve, reject) => {
        const have = window.Vatar;
        if (have) {
            resolve(have);
            return;
        }
        const s = document.createElement('script');
        s.src = sdkUrlFor(PAGE_URL);
        s.async = true;
        const timer = setTimeout(() => reject(new VatarClientError('sdk_timeout')), 10_000);
        s.onload = () => {
            clearTimeout(timer);
            const v = window.Vatar;
            if (v)
                resolve(v);
            else
                reject(new VatarClientError('sdk_missing'));
        };
        s.onerror = () => {
            clearTimeout(timer);
            reject(new VatarClientError('sdk_failed'));
        };
        document.head.appendChild(s);
    }).catch((e) => {
        loading = null;
        throw e;
    });
    return loading;
}
/** At most `max` calls per `windowMs`, counted locally. */
function throttle(max, windowMs) {
    const calls = [];
    return () => {
        const now = Date.now();
        while (calls.length && now - calls[0] > windowMs)
            calls.shift();
        if (calls.length >= max)
            throw new VatarClientError('rate_limited');
        calls.push(now);
    };
}
const initGate = throttle(10, 60_000);
const sessionGate = throttle(10, 60_000);
const purchaseGate = throttle(1, 3_000);
const purchasesGate = throttle(20, 60_000);
const scoreGate = throttle(1, 20_000);
const boardGate = throttle(12, 60_000);
const matchCreateGate = throttle(1, 5_000);
const matchJoinGate = throttle(1, 3_000);
/** One pay sheet at a time: a second tap while it's open gets a clean local "busy". */
let sheetOpen = false;
async function call(f) {
    const s = await sdk();
    try {
        return await f(s);
    }
    catch (e) {
        const code = e.code;
        throw new VatarClientError(typeof code === 'string' ? code : 'vatar_error');
    }
}
export const vatar = {
    init() {
        initGate();
        return call((s) => s.init());
    },
    /** A fresh 15-minute session token for our server to verify. */
    session() {
        sessionGate();
        return call((s) => s.session());
    },
    /**
     * Open Lagos Life's own pay sheet for a product. Resolving is NOT proof of payment: send the
     * purchaseId to our server, which checks it with Vatar before giving anything.
     */
    async purchase(productId) {
        if (sheetOpen)
            throw new VatarClientError('busy');
        purchaseGate();
        sheetOpen = true;
        try {
            return await call((s) => s.purchase(productId));
        }
        finally {
            sheetOpen = false;
        }
    },
    /** Paid purchases our server hasn't marked delivered yet (a crash or a trip to the payment page). */
    purchases() {
        purchasesGate();
        return call((s) => s.purchases());
    },
    /** Send a whole-number score; the board keeps each player's best. One per 20 s. */
    submitScore(n) {
        if (!Number.isInteger(n) || n < 0)
            return Promise.reject(new VatarClientError('bad_score'));
        scoreGate();
        return call((s) => s.submitScore(n));
    },
    /** The top 50 and your own place. At most 12 a minute; the board itself refreshes every 30 s. */
    leaderboard() {
        boardGate();
        return call((s) => s.leaderboard());
    },
    /** A purchase confirmed while no purchase() call was waiting for it. */
    async onPurchase(cb) {
        (await sdk()).onPurchase(cb);
    },
    /**
     * Staked matches. Every stake goes through Lagos Life's own confirm sheet; the stake comes off
     * the player's balance only on their tap there. Only our server posts the result.
     */
    match: {
        /** Open a stake table (one per 5 s). */
        async create(stake, seats = 4) {
            if (sheetOpen)
                throw new VatarClientError('busy');
            matchCreateGate();
            sheetOpen = true;
            try {
                return await call((s) => s.match.create({ stake, seats }));
            }
            finally {
                sheetOpen = false;
            }
        },
        /** Take a seat at a table by its code (one per 3 s). */
        async join(code) {
            if (sheetOpen)
                throw new VatarClientError('busy');
            matchJoinGate();
            sheetOpen = true;
            try {
                return await call((s) => s.match.join(code));
            }
            finally {
                sheetOpen = false;
            }
        },
        current() {
            return call((s) => s.match.current());
        },
        /** Leave before it starts and the stake comes back; after, it stays in the pot. */
        leave() {
            matchJoinGate();
            return call((s) => s.match.leave());
        },
        async onUpdate(cb) {
            return (await sdk()).match.onUpdate(cb);
        },
    },
    async onPause(cb) {
        (await sdk()).onPause(cb);
    },
    async onResume(cb) {
        (await sdk()).onResume(cb);
    },
};
//# sourceMappingURL=index.js.map